import { useState } from "react";
import {
  View,
  Text,
  ScrollView,
  Pressable,
  Platform,
  ActivityIndicator,
} from "react-native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import {
  ChevronLeft,
  Shield,
  Pencil,
  CheckCircle2,
  Clock,
  AlertCircle,
  Pill,
  Stethoscope,
  Snowflake,
} from "lucide-react-native";
import { EMBER } from "../../../onboarding/theme";
import type { CostsStackParamList } from "../../types";
import { useRegion, useTranslations } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import RegionGatePlaceholder from "../../components/RegionGatePlaceholder";
import { StatusBadge, type StatusBadgeTone } from "@louise/moon-ui";
import {
  useCoverage,
  type VerificationStatus,
} from "../../../../../packages/coverage/hooks/useCoverage";
import { useCoverageFinancials } from "../../../../../packages/coverage/hooks/useCoverageFinancials";
import GlassScreenLayout from "@louise/moon-ui/primitives/GlassScreenLayout";
import {
  GLASS_CARD_STYLE,
  SCREEN_PADDING_TOP,
} from "@louise/moon-ui/theme/tokens";
import InsuranceInfoModal from "../YouScreen/modals/InsuranceInfoModal";
import { useSmartBack } from "../../hooks/useSmartBack";

type Props = NativeStackScreenProps<CostsStackParamList, "Insurance">;

function VerificationBadge({ status }: { status: VerificationStatus }) {
  const config: Record<
    VerificationStatus,
    {
      label: string;
      tone: StatusBadgeTone;
      Icon: typeof CheckCircle2;
    }
  > = {
    verified: {
      label: "Verified",
      tone: "green",
      Icon: CheckCircle2,
    },
    pending: { label: "Pending", tone: "gold", Icon: Clock },
    unverified: {
      label: "Unverified",
      tone: "neutral",
      Icon: AlertCircle,
    },
  };

  const { label, tone, Icon } = config[status];

  return <StatusBadge label={label} tone={tone} icon={Icon} />;
}

function CoverageIndicator({
  icon: Icon,
  label,
  covered,
}: {
  icon: typeof Pill;
  label: string;
  covered: boolean;
}) {
  return (
    <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
      <Icon
        size={14}
        color={covered ? EMBER.colors.green : EMBER.colors.textFaint}
        strokeWidth={1.5}
      />
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 13,
          color: covered ? EMBER.colors.text : EMBER.colors.textFaint,
        }}
      >
        {label}: {covered ? "Covered" : "Not covered"}
      </Text>
    </View>
  );
}

export default function InsuranceScreen({ navigation, route }: Props) {
  const t = useTranslations();
  const region = useRegion();
  const capturePress = usePressCapture("InsuranceScreen");
  const {
    primaryPolicy,
    hasInsurance,
    verificationStatus,
    fertilityCoverage,
    isLoading,
  } = useCoverage();
  const financials = useCoverageFinancials();
  const [modalVisible, setModalVisible] = useState(false);
  const handleBack = useSmartBack({
    returnTab: route.params?.returnTab,
    fallbackTab: "Costs",
    fallbackScreen: "CostsOverview",
  });

  const carrierName =
    primaryPolicy?.payer?.displayName ??
    primaryPolicy?.payer?.name ??
    primaryPolicy?.insuranceCarrier ??
    "Insurance";

  if (region !== "US") return <RegionGatePlaceholder />;

  return (
    <GlassScreenLayout
      overlay={
        <InsuranceInfoModal
          visible={modalVisible}
          onClose={() => setModalVisible(false)}
          primaryPolicy={primaryPolicy}
          hasInsurance={hasInsurance}
          verificationStatus={verificationStatus}
          fertilityCoverage={fertilityCoverage}
          isLoading={isLoading}
        />
      }
    >
      <ScrollView
        contentContainerStyle={{
          paddingHorizontal: 16,
          paddingTop: SCREEN_PADDING_TOP,
          paddingBottom: 100,
          maxWidth: 520,
          width: "100%",
          alignSelf: "center",
        }}
        showsVerticalScrollIndicator={false}
      >
        {/* Back */}
        <Pressable
          onPress={() => {
            capturePress(
              pressLabel(
                route.params?.returnTab ? t("you.shared.back") : "Costs",
              ),
              "back",
            );
            handleBack();
          }}
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: 4,
            marginBottom: 8,
          }}
        >
          <ChevronLeft size={18} color={EMBER.colors.warm} strokeWidth={1.5} />
          <Text
            className="font-dm-sans"
            style={{ fontSize: 14, color: EMBER.colors.warm }}
          >
            {route.params?.returnTab ? t("you.shared.back") : "Costs"}
          </Text>
        </Pressable>

        {/* Title */}
        <Text
          className="font-dm-sans"
          style={
            {
              fontSize: 38,
              fontWeight: "700",
              color: "#3B1A08",
              lineHeight: 42,
              letterSpacing: -0.5,
              ...(Platform.OS === "web" && {
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.7' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='200' height='200' filter='url(%23n)' opacity='0.06'/%3E%3C/svg%3E"), linear-gradient(135deg, #3B1A08 0%, #7A3E15 35%, #3B1A08 60%, #6B3410 85%, #3B1A08 100%)`,
                backgroundSize: "200px 200px, 100% 100%",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }),
            } as Record<string, unknown>
          }
        >
          Insurance
        </Text>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 15,
            color: EMBER.colors.textLight,
            marginTop: 2,
            marginBottom: 20,
          }}
        >
          Your coverage details
        </Text>

        {/* Content */}
        {isLoading ? (
          <View
            style={{
              ...(GLASS_CARD_STYLE as object),
              padding: 40,
              alignItems: "center",
            }}
          >
            <ActivityIndicator color={EMBER.colors.textLight} />
          </View>
        ) : !hasInsurance ? (
          <View style={{ ...(GLASS_CARD_STYLE as object), padding: 20 }}>
            <View
              style={{
                alignItems: "center",
                gap: 12,
                paddingVertical: 20,
              }}
            >
              <View
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: 28,
                  backgroundColor: `${EMBER.colors.warm}14`,
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Shield size={28} color={EMBER.colors.warm} strokeWidth={1.5} />
              </View>
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 16,
                  fontWeight: "600",
                  color: EMBER.colors.text,
                }}
              >
                No Insurance on File
              </Text>
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 13,
                  color: EMBER.colors.textLight,
                  textAlign: "center",
                  lineHeight: 19,
                  maxWidth: 280,
                }}
              >
                Add your insurance to see what your plan covers for fertility
                treatments.
              </Text>
              <Pressable
                onPress={() => {
                  capturePress(pressLabel("Add insurance"));
                  setModalVisible(true);
                }}
                style={{
                  marginTop: 8,
                  paddingVertical: 12,
                  paddingHorizontal: 24,
                  borderRadius: 12,
                  backgroundColor: "#d6cbc3",
                }}
              >
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 14,
                    fontWeight: "600",
                    color: EMBER.colors.text,
                  }}
                >
                  Add insurance
                </Text>
              </Pressable>
            </View>
          </View>
        ) : (
          <View style={{ gap: 16 }}>
            {/* Policy Info Card */}
            <View style={{ ...(GLASS_CARD_STYLE as object), padding: 20 }}>
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: 16,
                }}
              >
                <View
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 10,
                  }}
                >
                  <Shield
                    size={18}
                    color={EMBER.colors.textLight}
                    strokeWidth={1.5}
                  />
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 16,
                      fontWeight: "600",
                      color: EMBER.colors.text,
                    }}
                  >
                    {carrierName}
                  </Text>
                  <VerificationBadge status={verificationStatus} />
                </View>
                <Pressable
                  onPress={() => {
                    capturePress(pressLabel("Edit insurance"));
                    setModalVisible(true);
                  }}
                  hitSlop={8}
                  accessibilityRole="button"
                  accessibilityLabel="Edit insurance"
                >
                  <Pencil
                    size={16}
                    color={EMBER.colors.textFaint}
                    strokeWidth={1.5}
                  />
                </Pressable>
              </View>

              <View style={{ gap: 8 }}>
                {primaryPolicy?.memberId && (
                  <View
                    style={{
                      flexDirection: "row",
                      justifyContent: "space-between",
                    }}
                  >
                    <Text
                      className="font-dm-sans"
                      style={{
                        fontSize: 13,
                        color: EMBER.colors.textFaint,
                      }}
                    >
                      Member ID
                    </Text>
                    <Text
                      className="font-dm-sans"
                      style={{
                        fontSize: 13,
                        fontWeight: "500",
                        color: EMBER.colors.text,
                      }}
                    >
                      {primaryPolicy.memberId}
                    </Text>
                  </View>
                )}
                {primaryPolicy?.groupNumber && (
                  <View
                    style={{
                      flexDirection: "row",
                      justifyContent: "space-between",
                    }}
                  >
                    <Text
                      className="font-dm-sans"
                      style={{
                        fontSize: 13,
                        color: EMBER.colors.textFaint,
                      }}
                    >
                      Group Number
                    </Text>
                    <Text
                      className="font-dm-sans"
                      style={{
                        fontSize: 13,
                        fontWeight: "500",
                        color: EMBER.colors.text,
                      }}
                    >
                      {primaryPolicy.groupNumber}
                    </Text>
                  </View>
                )}
                {primaryPolicy?.planType && (
                  <View
                    style={{
                      flexDirection: "row",
                      justifyContent: "space-between",
                    }}
                  >
                    <Text
                      className="font-dm-sans"
                      style={{
                        fontSize: 13,
                        color: EMBER.colors.textFaint,
                      }}
                    >
                      Plan
                    </Text>
                    <Text
                      className="font-dm-sans"
                      style={{
                        fontSize: 13,
                        fontWeight: "500",
                        color: EMBER.colors.text,
                      }}
                    >
                      {primaryPolicy.planType}
                      {primaryPolicy.planName
                        ? ` \u00b7 ${primaryPolicy.planName}`
                        : ""}
                    </Text>
                  </View>
                )}
              </View>
            </View>

            {/* Coverage Financials Card */}
            {verificationStatus === "verified" &&
              fertilityCoverage?.hasCoverage && (
                <View style={{ ...(GLASS_CARD_STYLE as object), padding: 20 }}>
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 15,
                      fontWeight: "600",
                      color: EMBER.colors.text,
                      marginBottom: 16,
                    }}
                  >
                    Coverage Details
                  </Text>

                  {/* Financials Grid */}
                  <View
                    style={{
                      flexDirection: "row",
                      flexWrap: "wrap",
                      gap: 10,
                      marginBottom: 16,
                    }}
                  >
                    {financials.deductible && (
                      <View
                        style={{
                          minWidth: "45%",
                          backgroundColor: "rgba(255,255,255,0.15)",
                          borderRadius: 10,
                          padding: 12,
                        }}
                      >
                        <Text
                          className="font-dm-sans"
                          style={{
                            fontSize: 10,
                            color: EMBER.colors.textFaint,
                            textTransform: "uppercase",
                            letterSpacing: 0.5,
                          }}
                        >
                          Deductible
                        </Text>
                        <Text
                          className="font-dm-sans"
                          style={{
                            fontSize: 18,
                            fontWeight: "600",
                            color: EMBER.colors.text,
                            marginTop: 4,
                          }}
                        >
                          {financials.deductible}
                        </Text>
                      </View>
                    )}
                    {financials.outOfPocketMax && (
                      <View
                        style={{
                          minWidth: "45%",
                          backgroundColor: "rgba(255,255,255,0.15)",
                          borderRadius: 10,
                          padding: 12,
                        }}
                      >
                        <Text
                          className="font-dm-sans"
                          style={{
                            fontSize: 10,
                            color: EMBER.colors.textFaint,
                            textTransform: "uppercase",
                            letterSpacing: 0.5,
                          }}
                        >
                          OOP Max
                        </Text>
                        <Text
                          className="font-dm-sans"
                          style={{
                            fontSize: 18,
                            fontWeight: "600",
                            color: EMBER.colors.text,
                            marginTop: 4,
                          }}
                        >
                          {financials.outOfPocketMax}
                        </Text>
                      </View>
                    )}
                    {financials.coinsurancePercent && (
                      <View
                        style={{
                          minWidth: "45%",
                          backgroundColor: "rgba(255,255,255,0.15)",
                          borderRadius: 10,
                          padding: 12,
                        }}
                      >
                        <Text
                          className="font-dm-sans"
                          style={{
                            fontSize: 10,
                            color: EMBER.colors.textFaint,
                            textTransform: "uppercase",
                            letterSpacing: 0.5,
                          }}
                        >
                          Coinsurance
                        </Text>
                        <Text
                          className="font-dm-sans"
                          style={{
                            fontSize: 18,
                            fontWeight: "600",
                            color: EMBER.colors.text,
                            marginTop: 4,
                          }}
                        >
                          {financials.coinsurancePercent}
                        </Text>
                      </View>
                    )}
                    {financials.treatmentsCovered.length > 0 && (
                      <View
                        style={{
                          minWidth: "45%",
                          backgroundColor: "rgba(255,255,255,0.15)",
                          borderRadius: 10,
                          padding: 12,
                        }}
                      >
                        <Text
                          className="font-dm-sans"
                          style={{
                            fontSize: 10,
                            color: EMBER.colors.textFaint,
                            textTransform: "uppercase",
                            letterSpacing: 0.5,
                          }}
                        >
                          Treatments
                        </Text>
                        <Text
                          className="font-dm-sans"
                          style={{
                            fontSize: 18,
                            fontWeight: "600",
                            color: EMBER.colors.green,
                            marginTop: 4,
                          }}
                        >
                          {financials.treatmentsCovered.length} covered
                        </Text>
                      </View>
                    )}
                  </View>

                  {/* Coverage Indicators */}
                  <View style={{ gap: 8 }}>
                    {financials.medsCovered != null && (
                      <CoverageIndicator
                        icon={Pill}
                        label="Medications"
                        covered={financials.medsCovered}
                      />
                    )}
                    {financials.diagnosticsCovered != null && (
                      <CoverageIndicator
                        icon={Stethoscope}
                        label="Diagnostics"
                        covered={financials.diagnosticsCovered}
                      />
                    )}
                    {financials.cryostorageCovered != null && (
                      <CoverageIndicator
                        icon={Snowflake}
                        label="Cryostorage"
                        covered={financials.cryostorageCovered}
                      />
                    )}
                  </View>

                  {/* Treatment Limits */}
                  {financials.treatmentLimits.length > 0 && (
                    <View style={{ marginTop: 12 }}>
                      {financials.treatmentLimits.map((limit, i) => (
                        <Text
                          key={i}
                          className="font-dm-sans"
                          style={{
                            fontSize: 12,
                            color: EMBER.colors.textLight,
                            lineHeight: 18,
                          }}
                        >
                          {limit}
                        </Text>
                      ))}
                    </View>
                  )}
                </View>
              )}

            {/* Pending / Unverified Status */}
            {verificationStatus === "pending" && (
              <View style={{ ...(GLASS_CARD_STYLE as object), padding: 20 }}>
                <View
                  style={{
                    padding: 12,
                    borderRadius: 10,
                    backgroundColor: `${EMBER.colors.gold}10`,
                  }}
                >
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 13,
                      color: EMBER.colors.gold,
                      lineHeight: 19,
                    }}
                  >
                    We{"\u2019"}re verifying your coverage. This usually takes 1
                    {"\u2013"}2 business days. You{"\u2019"}ll be notified once
                    verification is complete.
                  </Text>
                </View>
              </View>
            )}

            {verificationStatus === "unverified" && (
              <View style={{ ...(GLASS_CARD_STYLE as object), padding: 20 }}>
                <View
                  style={{
                    padding: 12,
                    borderRadius: 10,
                    backgroundColor: "rgba(255,255,255,0.1)",
                  }}
                >
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 13,
                      color: EMBER.colors.textLight,
                      lineHeight: 19,
                    }}
                  >
                    Submit your insurance details and we{"\u2019"}ll verify
                    coverage with your provider.
                  </Text>
                </View>
              </View>
            )}
          </View>
        )}
      </ScrollView>
    </GlassScreenLayout>
  );
}
